/**
 * File: useHelpDialogController.ts
 * Path: apps/admin/src/modules/layout/hooks/useHelpDialogController.ts
 * Description: The Help dialog: the tutorials for the page being shown (or
 * every tutorial that matches the search), starting one, and going to the
 * How to page.
 */

import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ADMIN_PATHS } from "@idk/config";
import { useHelp, useTour } from "@/shared/hooks/useTourState";
import { TUTORIALS } from "@/shared/utils/tourContentData";
import type { Tutorial } from "@/shared/utils/tutorialTypes";
import {
  groupTutorials,
  pageTitleFor,
  searchTutorials,
  stepsToRun,
  tutorialsForPage,
} from "@/shared/utils/tutorialUtils";

export const useHelpDialogController = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { open, closeHelp } = useHelp();
  const start = useTour((s) => s.start);
  const [query, setQuery] = useState("");

  const searching = query.trim().length > 0;
  const shown = searching ? searchTutorials(query, pathname) : tutorialsForPage(pathname);

  const close = () => {
    setQuery("");
    closeHelp();
  };

  return {
    open,
    pageTitle: pageTitleFor(pathname),
    query,
    setQuery,
    searching,
    groups: groupTutorials(shown),
    total: TUTORIALS.length,
    onOpenChange: (next: boolean) => {
      if (!next) close();
    },
    run: (tutorial: Tutorial) => {
      close();
      start(stepsToRun(tutorial, pathname), tutorial.title);
    },
    seeEvery: () => {
      close();
      navigate(ADMIN_PATHS.accounting.guide);
    },
  };
};
